import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/axiosConfig";
import localforage from "localforage";
import Dashboard from "./Dashboard";

const API_URL = process.env.REACT_APP_API_URL;

const INK = "#1e2a3a";
const MUTED = "#6b7a8d";
const LINE = "#e6ecf3";
const BLUE = "#2563eb";
const INDIGO = "#5b5bd6";
const GREEN = "#12a150";
const AMBER = "#e5a13a";
const RED = "#e5484d";
const BG = "#f7f9fc";

const card = { background: "#fff", border: `1px solid ${LINE}`, borderRadius: 14, padding: 18 };
const secHead = { fontSize: 11, fontWeight: 800, color: MUTED, letterSpacing: ".6px", marginBottom: 8 };
const chip = { border: `1px solid ${LINE}`, borderRadius: 99, padding: "4px 11px", fontSize: 12.5,
               color: INK, background: "#fff" };
const td = { padding: "9px 10px", borderBottom: `1px solid ${LINE}`, verticalAlign: "top", color: "#374151" };
const tdNowrap = { ...td, whiteSpace: "nowrap" };
const tdNum = { ...td, textAlign: "center", width: 40 };

const TYPE_LABEL = {
  discussion: "Discussion",
  stc_actionable: "STC Actionable",
  leader_actionable: "Leader Actionable",
  remark: "Remark",
};
const TYPE_COLOR = {
  discussion: BLUE,
  stc_actionable: GREEN,
  leader_actionable: INDIGO,
  remark: AMBER,
};

const SAMPLES = [
  "What are the recurring booth committee problems?",
  "Which constituencies report water or road issues most often?",
  "Where is the caste census work lagging and why?",
  "What complaints were raised about local leadership coordination?",
  "Which welfare schemes are most frequently mentioned by respondents?",
];

const AskFieldData = () => {
  const [geo, setGeo] = useState({});
  const [zone, setZone] = useState("");
  const [pc, setPc] = useState("");
  const [ac, setAc] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [question, setQuestion] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState(null);
  const [showSources, setShowSources] = useState(true);
  const [hl, setHl] = useState(null); // highlighted source row

  const authHeader = async () => {
    const token = await localforage.getItem("token");
    return { Authorization: "Bearer " + token };
  };

  useEffect(() => {
    (async () => {
      try {
        const headers = await authHeader();
        const [g, s] = await Promise.all([
          api.get(`${API_URL}/mom-format/geo-hierarchy`, { headers }),
          api.get(`${API_URL}/mom-rag/status`, { headers }),
        ]);
        setGeo(g.data || {});
        setStatus(s.data || null);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const zones = useMemo(() => Object.keys(geo).sort(), [geo]);
  const pcs = useMemo(() => (zone && geo[zone] ? Object.keys(geo[zone]).sort() : []), [geo, zone]);
  const acs = useMemo(
    () => (zone && pc && geo[zone] && geo[zone][pc] ? geo[zone][pc] : []), [geo, zone, pc]);

  const ask = async (q) => {
    const text = (q != null ? q : question).trim();
    if (text.length < 4) { setErr("Please enter a question."); return; }
    setQuestion(text);
    setLoading(true); setErr(""); setRes(null);
    try {
      const headers = await authHeader();
      const body = { question: text };
      if (zone) body.zone = zone;
      if (pc) body.pc = pc;
      if (ac) body.ac = ac;
      if (from) body.from = from;
      if (to) body.to = to;
      const r = await api.post(`${API_URL}/mom-rag/ask`, body, { headers, timeout: 180000 });
      setRes(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || "Could not get an answer.");
    } finally { setLoading(false); }
  };

  const jumpTo = (n) => {
    setShowSources(true);
    setHl(n);
    setTimeout(() => {
      const el = document.getElementById(`src-row-${n}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
    setTimeout(() => setHl(null), 3500);
  };

  const Cite = ({ n }) => {
    const src = res && res.sources ? res.sources[n - 1] : null;
    return (
      <button type="button" onClick={() => jumpTo(n)}
        title={src ? `${src.pcm} · ${src.ac || ""} · ${src.date || ""}` : `Source ${n}`}
        style={{ background: "#eaf2fe", color: BLUE, border: "none", borderRadius: 6,
                 padding: "1px 7px", fontWeight: 800, fontSize: 11, margin: "0 2px",
                 cursor: "pointer", verticalAlign: "1px" }}>
        {n}
      </button>
    );
  };

  // inline: **bold** and [n] citations, rendered as React nodes (no raw HTML)
  const inline = (txt, keyBase) => String(txt).split(/(\*\*[^*]+\*\*|\[\d+\])/g).map((part, i) => {
    const k = `${keyBase}-${i}`;
    const b = part.match(/^\*\*([^*]+)\*\*$/);
    if (b) return <strong key={k} style={{ color: INK }}>{b[1]}</strong>;
    const c = part.match(/^\[(\d+)\]$/);
    if (c) return <Cite key={k} n={Number(c[1])} />;
    return <span key={k}>{part}</span>;
  });

  // block-level fallback for when the model returns markdown instead of JSON
  const renderMarkdown = (txt) => {
    const lines = String(txt).split(/\n/);
    const out = [];
    let list = [];
    const flush = () => {
      if (list.length) {
        out.push(
          <ul key={`ul-${out.length}`} style={{ margin: "6px 0 10px", paddingLeft: 20, lineHeight: 1.75 }}>
            {list.map((li, i) => <li key={i}>{inline(li, `li-${out.length}-${i}`)}</li>)}
          </ul>
        );
        list = [];
      }
    };
    lines.forEach((ln, i) => {
      const t = ln.trim();
      const bullet = t.match(/^(?:[-*•]|\d+[.)])\s+(.*)$/);
      if (bullet) { list.push(bullet[1]); return; }
      flush();
      if (!t) return;
      const head = t.match(/^#{1,6}\s+(.*)$/);
      if (head) {
        out.push(<div key={`h-${i}`} style={{ fontWeight: 800, color: INK, margin: "10px 0 4px" }}>
          {inline(head[1], `h-${i}`)}</div>);
        return;
      }
      out.push(<p key={`p-${i}`} style={{ margin: "0 0 8px", lineHeight: 1.75 }}>{inline(t, `p-${i}`)}</p>);
    });
    flush();
    return out;
  };

  const resetAll = () => {
    setZone(""); setPc(""); setAc(""); setFrom(""); setTo("");
    setQuestion(""); setRes(null); setErr(""); setShowSources(true);
  };

  const downloadSources = () => {
    if (!res || !res.sources) return;
    const esc = (v) => {
      const t = v == null ? "" : String(v);
      return /[",\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t;
    };
    const head = ["#", "Point / Statement", "Type", "Actionable", "Timeline", "PCM",
                  "Assembly Const.", "Parliament Const.", "Zone", "Meeting Date",
                  "Respondent", "Designation", "Match %"];
    const rows = res.sources.map((s) => [
      s.n, s.text, TYPE_LABEL[s.chunkType] || s.chunkType, s.actionable, s.timeline, s.pcm,
      s.ac, s.pc, s.zone, s.date, s.respondent, s.designation,
      s.score != null ? Math.round(s.score * 100) : "",
    ]);
    const csv = [head, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "field-data-sources.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const sel = { padding: "8px 10px", border: `1px solid ${LINE}`, borderRadius: 9,
                fontSize: 13, background: "#fff", color: INK, width: "100%" };
  const lbl = { fontSize: 11, fontWeight: 700, color: MUTED, display: "block", marginBottom: 4 };

  return (
    <div>
      <Dashboard />
      <div style={{ background: BG, minHeight: "100vh", padding: "18px 24px" }}>
        <div style={{ width: "100%", maxWidth: "none", margin: 0 }}>

          <div style={{ ...card, marginBottom: 14 }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: INK }}>Ask the Field Data</div>
            <div style={{ fontSize: 13, color: MUTED }}>
              Ask any question about recorded meetings — answers are drawn from the actual records, with sources.
            </div>

            {status && (
              <div style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>
                Index: <strong style={{ color: INK }}>{status.indexedMeetings}</strong> of{" "}
                <strong style={{ color: INK }}>{status.meetings}</strong> meetings
                {" "}({status.chunks} searchable points)
                {status.pending > 0 && (
                  <span style={{ color: AMBER, fontWeight: 700 }}> · {status.pending} not yet indexed</span>
                )}
                {status.ai && status.ai.provider === "none" && (
                  <span style={{ color: AMBER, fontWeight: 700 }}> · AI answer disabled (no API key)</span>
                )}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
              <input
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
                placeholder="e.g. What are the recurring booth committee problems in Srikakulam?"
                style={{ ...sel, flex: "1 1 420px", padding: "11px 13px", fontSize: 14 }}
              />
              <button type="button" onClick={() => ask()} disabled={loading}
                style={{ background: BLUE, color: "#fff", border: "none", borderRadius: 10,
                         padding: "11px 24px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
                {loading ? "Thinking…" : "Ask"}
              </button>
              <button type="button" onClick={resetAll} disabled={loading}
                title="Clear the question, all filters and the results"
                style={{ background: "#eef2f7", color: INK, border: `1px solid ${LINE}`, borderRadius: 10,
                         padding: "11px 20px", fontWeight: 600, cursor: "pointer", fontSize: 14 }}>
                Reset
              </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                          gap: 10, marginTop: 12 }}>
              <div>
                <label style={lbl}>ZONE</label>
                <select value={zone} onChange={(e) => { setZone(e.target.value); setPc(""); setAc(""); }} style={sel}>
                  <option value="">All Zones</option>
                  {zones.map((z) => <option key={z} value={z}>{z}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>PARLIAMENT CONST.</label>
                <select value={pc} onChange={(e) => { setPc(e.target.value); setAc(""); }} style={sel} disabled={!zone}>
                  <option value="">All PCs</option>
                  {pcs.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>ASSEMBLY CONST.</label>
                <select value={ac} onChange={(e) => setAc(e.target.value)} style={sel} disabled={!pc}>
                  <option value="">All ACs</option>
                  {acs.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>FROM</label>
                <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={sel} />
              </div>
              <div>
                <label style={lbl}>TO</label>
                <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={sel} />
              </div>
            </div>

            {(zone || pc || ac || from || to) && (
              <div style={{ marginTop: 10, fontSize: 12, color: MUTED, display: "flex",
                            gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span>Filtering by:</span>
                {[zone, pc, ac, from && `from ${from}`, to && `to ${to}`].filter(Boolean).map((f) => (
                  <span key={f} style={{ background: "#eef2ff", color: INDIGO, borderRadius: 99,
                                         padding: "2px 10px", fontWeight: 700 }}>{f}</span>
                ))}
                <button type="button"
                  onClick={() => { setZone(""); setPc(""); setAc(""); setFrom(""); setTo(""); }}
                  style={{ background: "none", border: "none", color: BLUE, fontWeight: 700,
                           cursor: "pointer", fontSize: 12, padding: 0 }}>
                  Clear filters
                </button>
              </div>
            )}

            <div style={{ marginTop: 12, display: "flex", gap: 7, flexWrap: "wrap" }}>
              {SAMPLES.map((s) => (
                <button key={s} type="button" onClick={() => ask(s)} disabled={loading}
                  style={{ background: "#f1f5f9", color: INK, border: `1px solid ${LINE}`,
                           borderRadius: 99, padding: "5px 12px", fontSize: 12,
                           cursor: "pointer", whiteSpace: "nowrap" }}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          {err && <p style={{ color: RED, textAlign: "center", fontWeight: 600 }}>{err}</p>}
          {loading && (
            <p style={{ textAlign: "center", color: MUTED }}>
              Searching the meeting records and composing an answer…
            </p>
          )}

          {res && !loading && (
            <>
              {res.empty ? (
                <div style={{ ...card, color: MUTED, textAlign: "center" }}>{res.note}</div>
              ) : (
                <>
                  <div style={{ ...card, borderLeft: `5px solid ${INDIGO}`, marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: INDIGO,
                                  letterSpacing: ".6px", marginBottom: 8 }}>
                      ANSWER
                    </div>
                    {res.structured ? (
                      <div>
                        {res.structured.headline && (
                          <div style={{ fontSize: 16.5, fontWeight: 700, color: INK, lineHeight: 1.55,
                                        marginBottom: 16 }}>
                            {res.structured.headline}
                          </div>
                        )}

                        {res.structured.findings.length > 0 && (
                          <>
                            <div style={secHead}>KEY FINDINGS</div>
                            <div style={{ display: "grid", gap: 10, marginBottom: 16 }}>
                              {res.structured.findings.map((f, i) => (
                                <div key={i} style={{ display: "flex", gap: 12, border: `1px solid ${LINE}`,
                                                      borderRadius: 10, padding: "12px 14px", background: "#fbfcff" }}>
                                  <div style={{ flexShrink: 0, width: 26, height: 26, borderRadius: "50%",
                                                background: INDIGO, color: "#fff", fontWeight: 800,
                                                fontSize: 12.5, display: "flex", alignItems: "center",
                                                justifyContent: "center" }}>
                                    {i + 1}
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 800, color: INK, fontSize: 14.5, marginBottom: 3 }}>
                                      {f.title}
                                    </div>
                                    <div style={{ fontSize: 13.5, color: "#374151", lineHeight: 1.65 }}>{f.detail}</div>
                                    {f.sources.length > 0 && (
                                      <div style={{ marginTop: 6, fontSize: 11.5, color: MUTED }}>
                                        Sources: {f.sources.map((n) => <Cite key={n} n={n} />)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        )}

                        {(res.structured.constituencies.length > 0 || res.structured.pcms.length > 0) && (
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                                        gap: 14, marginBottom: 16 }}>
                            {res.structured.constituencies.length > 0 && (
                              <div>
                                <div style={secHead}>CONSTITUENCIES INVOLVED</div>
                                <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                                  {res.structured.constituencies.map((c) => (
                                    <span key={c.name} style={chip}>{c.name} <b style={{ color: INDIGO }}>{c.count}</b></span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {res.structured.pcms.length > 0 && (
                              <div>
                                <div style={secHead}>REPORTED BY</div>
                                <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                                  {res.structured.pcms.map((c) => (
                                    <span key={c.name} style={chip}>{c.name} <b style={{ color: INDIGO }}>{c.count}</b></span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {res.structured.actions.length > 0 && (
                          <div style={{ background: "#f2f7ff", border: "1px solid #d6e6ff", borderRadius: 10,
                                        padding: "12px 16px", marginBottom: 12 }}>
                            <div style={{ ...secHead, color: BLUE }}>RECOMMENDED ACTIONS</div>
                            <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13.5, color: "#1e3a5f", lineHeight: 1.8 }}>
                              {res.structured.actions.map((a, i) => <li key={i}>{a}</li>)}
                            </ol>
                          </div>
                        )}

                        {res.structured.gaps && (
                          <div style={{ fontSize: 12.5, color: MUTED, fontStyle: "italic" }}>
                            <strong style={{ fontStyle: "normal" }}>Not covered by these records:</strong>{" "}
                            {res.structured.gaps}
                          </div>
                        )}
                      </div>
                    ) : res.answer ? (
                      <div style={{ fontSize: 14, color: "#2b3646" }}>{renderMarkdown(res.answer)}</div>
                    ) : res.aiEnabled ? (
                      <div style={{ fontSize: 13.5, color: RED, margin: 0, lineHeight: 1.6 }}>
                        <strong>The AI service could not generate an answer.</strong>{" "}
                        <span style={{ color: MUTED }}>
                          {res.aiError || "No reason was returned."} The matching records are listed below.
                        </span>
                      </div>
                    ) : (
                      <p style={{ fontSize: 13.5, color: MUTED, margin: 0 }}>
                        No AI key is configured on the server, so no narrative answer was generated —
                        the most relevant records are listed below.
                      </p>
                    )}
                    <div style={{ fontSize: 11, color: MUTED, marginTop: 10 }}>
                      Based on {res.sources.length} matching record
                      {res.sources.length === 1 ? "" : "s"} you are permitted to see
                      {res.mode === "memory" ? " · similarity search" : " · vector index"}
                      {res.aiModel ? ` · answered by ${res.aiModel.split(":").pop()}` : ""}
                    </div>
                  </div>

                  <div style={{ ...card }}>
                    <div style={{ display: "flex", justifyContent: "space-between",
                                  alignItems: "center", marginBottom: 10 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: INK }}>
                        Source records ({res.sources.length})
                      </div>
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <button type="button" onClick={downloadSources}
                        style={{ background: GREEN, color: "#fff", border: "none", borderRadius: 8,
                                 padding: "6px 12px", fontWeight: 700, cursor: "pointer", fontSize: 12 }}>
                        Download CSV
                      </button>
                      <button type="button" onClick={() => setShowSources((v) => !v)}
                        style={{ background: "none", border: "none", color: BLUE,
                                 fontWeight: 700, cursor: "pointer", fontSize: 12.5 }}>
                        {showSources ? "Hide ▲" : "Show ▼"}
                      </button>
                      </div>
                    </div>

                    {showSources && (
                      <div style={{ overflowX: "auto", border: `1px solid ${LINE}`, borderRadius: 10 }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 1100 }}>
                          <thead>
                            <tr style={{ background: "#f2f5fa" }}>
                              {["#", "Point / Statement", "Type", "Actionable", "Timeline", "PCM",
                                "Assembly Const.", "Parliament Const.", "Zone", "Meeting Date",
                                "Respondent", "Match"].map((h) => (
                                <th key={h} style={{
                                  padding: "9px 10px", textAlign: "left", fontSize: 11,
                                  fontWeight: 800, color: MUTED, letterSpacing: ".3px",
                                  borderBottom: `1px solid ${LINE}`, whiteSpace: "nowrap",
                                  position: "sticky", top: 0, background: "#f2f5fa",
                                }}>
                                  {h.toUpperCase()}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {res.sources.map((s, i) => (
                              <tr key={s.n} id={`src-row-${s.n}`}
                                style={{ background: hl === s.n ? "#fff6d6" : i % 2 ? "#fbfcfe" : "#fff",
                                         transition: "background .4s" }}>
                                <td style={tdNum}>
                                  <span style={{ background: "#eaf2fe", color: BLUE, borderRadius: 6,
                                                 padding: "2px 7px", fontSize: 11, fontWeight: 800 }}>
                                    {s.n}
                                  </span>
                                </td>
                                <td style={{ ...td, minWidth: 380, color: INK, lineHeight: 1.5 }}>{s.text}</td>
                                <td style={td}>
                                  <span style={{
                                    background: `${TYPE_COLOR[s.chunkType] || MUTED}18`,
                                    color: TYPE_COLOR[s.chunkType] || MUTED,
                                    borderRadius: 6, padding: "2px 8px", fontSize: 11,
                                    fontWeight: 700, whiteSpace: "nowrap",
                                  }}>
                                    {TYPE_LABEL[s.chunkType] || s.chunkType || "—"}
                                  </span>
                                </td>
                                <td style={{ ...td, minWidth: 200 }}>{s.actionable || "—"}</td>
                                <td style={tdNowrap}>{s.timeline || "—"}</td>
                                <td style={{ ...tdNowrap, color: INDIGO, fontWeight: 700 }}>{s.pcm}</td>
                                <td style={tdNowrap}>{s.ac || "—"}</td>
                                <td style={tdNowrap}>{s.pc || "—"}</td>
                                <td style={tdNowrap}>{s.zone || "—"}</td>
                                <td style={tdNowrap}>{s.date || "—"}</td>
                                <td style={{ ...td, minWidth: 140 }}>
                                  {s.respondent || "—"}
                                  {s.designation ? (
                                    <div style={{ fontSize: 11, color: MUTED }}>{s.designation}</div>
                                  ) : null}
                                </td>
                                <td style={tdNowrap}>
                                  {s.score != null ? (
                                    <span style={{ fontWeight: 700, color: s.score >= 0.5 ? GREEN : s.score >= 0.35 ? AMBER : MUTED }}>
                                      {Math.round(s.score * 100)}%
                                    </span>
                                  ) : "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AskFieldData;
